import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  addSalonSpeakFavorite,
  createSalonSpeakAttempt,
  getSalonSpeakMe,
  removeSalonSpeakFavorite,
  updateSalonSpeakMe,
} from "../api/salonSpeak";
import {
  SALON_SPEAK_HELP_SECTIONS,
  SALON_SPEAK_TOOLTIPS,
  SALON_SPEAK_TROUBLESHOOTING,
} from "../constants/salonSpeakTooltips";
import { getBusinessProfile } from "../api/onboarding";
import content from "../data/salonSpeakReception/content.json";
import SalonSpeakHome from "./salonspeak/Home";
import SalonSpeakLessons from "./salonspeak/Lessons";
import SalonSpeakRoleplay from "./salonspeak/Roleplay";
import SalonSpeakVocabulary from "./salonspeak/Vocabulary";
import SalonSpeakQuickPhrases from "./salonspeak/QuickPhrases";
import {
  isSalonSpeakVoiceChoice,
  setSalonSpeakVoicePreference,
} from "./salonspeak/speechUtils";
import "./HrSectionCompact.css";

const BASE_PATHS = [
  "/manager/hr/training/salonspeak-reception",
  "/manager/hr/salonspeak-reception",
];
const MODULE_OPTIONS = { module: "reception" };

const tabs = [
  { id: "home", label: "Home" },
  { id: "lessons", label: "Lessons" },
  { id: "roleplay", label: "Roleplay" },
  { id: "vocab", label: "Vocab" },
  { id: "phrases", label: "Phrases" },
];

const favoriteKey = (type, id) => `${type}:${id}`;

function resolveBasePath(pathname) {
  const match = BASE_PATHS.find((base) => pathname.startsWith(base));
  return match || BASE_PATHS[0];
}

function resolveTab(pathname, basePath) {
  if (!pathname.startsWith(basePath)) return "home";
  const part = pathname.slice(basePath.length).replace(/^\/+/, "").split("/")[0];
  if (tabs.some((item) => item.id === part)) return part;
  return "home";
}

function getFirstName(user) {
  const raw = String(user?.full_name || user?.name || "").trim();
  if (!raw) return "";
  return raw.split(/\s+/)[0] || "";
}

function lessonDraftKey(userKey) {
  return `salonspeak-reception:pending-completed-lessons:${String(userKey || "anonymous")}`;
}

const LESSON_DRAFT_KEY = "salonspeak-reception:pending-completed-lessons";
const LESSON_DRAFT_KEY_PREFIX = "salonspeak-reception:pending-completed-lessons:";

function getDraftStorageKeys(userKey) {
  const keys = new Set([LESSON_DRAFT_KEY, lessonDraftKey(userKey), lessonDraftKey("anonymous")]);
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(LESSON_DRAFT_KEY_PREFIX)) {
        keys.add(key);
      }
    }
  } catch (err) {
    // no-op
  }
  return Array.from(keys);
}

function normalizeLessonIds(lessonIds) {
  return Array.from(
    new Set((lessonIds || []).map((item) => String(item || "").trim()).filter(Boolean))
  );
}

function readPendingCompletedLessons(userKey) {
  try {
    const merged = [];
    getDraftStorageKeys(userKey).forEach((key) => {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      merged.push(...parsed);
    });
    return normalizeLessonIds(merged);
  } catch (err) {
    return [];
  }
}

function writePendingCompletedLessons(userKey, lessonIds) {
  const unique = normalizeLessonIds(lessonIds);
  const keys = getDraftStorageKeys(userKey);
  if (!unique.length) {
    keys.forEach((key) => localStorage.removeItem(key));
    return;
  }
  localStorage.setItem(LESSON_DRAFT_KEY, JSON.stringify(unique));
  keys.forEach((key) => {
    if (key !== LESSON_DRAFT_KEY) {
      localStorage.removeItem(key);
    }
  });
}

function addPendingCompletedLesson(userKey, lessonId) {
  const pending = readPendingCompletedLessons(userKey);
  if (!pending.includes(lessonId)) {
    pending.push(lessonId);
  }
  writePendingCompletedLessons(userKey, pending);
}

function removePendingCompletedLesson(userKey, lessonId) {
  const pending = readPendingCompletedLessons(userKey);
  writePendingCompletedLessons(
    userKey,
    pending.filter((item) => item !== lessonId)
  );
}

function mergeLessonProgress(progressBlob, lessonIds) {
  const base = progressBlob && typeof progressBlob === "object" ? { ...progressBlob } : {};
  const completed = Array.isArray(base.completed_lessons) ? normalizeLessonIds(base.completed_lessons) : [];
  const mergedCompleted = normalizeLessonIds([...completed, ...(lessonIds || [])]);
  base.completed_lessons = mergedCompleted;
  if (mergedCompleted.length) {
    base.last_lesson_id = mergedCompleted[mergedCompleted.length - 1];
  }
  return base;
}

function mergeMeWithLessons(payload, lessonIds) {
  if (!payload || !lessonIds?.length) return payload;
  return {
    ...payload,
    lesson_progress_json: mergeLessonProgress(payload.lesson_progress_json, lessonIds),
  };
}

export default function SalonSpeakReception() {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = resolveBasePath(location.pathname || "");
  const activeTab = resolveTab(location.pathname || "", basePath);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [salonMode, setSalonMode] = useState(false);
  const [selectedVoiceUri, setSelectedVoiceUri] = useState("");
  const [savedVoiceUri, setSavedVoiceUri] = useState("");
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");
  const [setupTimezone, setSetupTimezone] = useState("");
  const lessonDraftUserKey = String(user?.id || user?.user_id || user?.email || "anonymous");

  const firstName = getFirstName(user);

  const fetchMe = useCallback(async () => {
    if (!token) return;
    try {
      const [payload, profile] = await Promise.all([
        getSalonSpeakMe(token, MODULE_OPTIONS),
        getBusinessProfile(token).catch(() => null),
      ]);
      const pendingCompletedLessons = readPendingCompletedLessons(lessonDraftUserKey);
      setMe(mergeMeWithLessons(payload, pendingCompletedLessons));
      const profileTimezone = String(profile?.timezone || "").trim();
      if (profileTimezone) {
        setSetupTimezone(profileTimezone);
      }
      const nextFavorites = new Set(
        (payload?.favorites || []).map((item) =>
          favoriteKey(item.favorite_type, item.content_id)
        )
      );
      setFavorites(nextFavorites);
      const settings = payload?.lesson_progress_json?.settings || {};
      setSalonMode(Boolean(settings.salon_mode));
      const preferredVoiceUri = String(settings.speech_voice_uri || "").trim().toLowerCase();
      const normalizedSavedVoiceUri = isSalonSpeakVoiceChoice(preferredVoiceUri)
        ? preferredVoiceUri
        : "";
      setSelectedVoiceUri(normalizedSavedVoiceUri);
      setSavedVoiceUri(normalizedSavedVoiceUri);
      setSalonSpeakVoicePreference(normalizedSavedVoiceUri);
      setVoiceNotice("");
      setError("");

      if (pendingCompletedLessons.length) {
        try {
          const synced = await updateSalonSpeakMe(
            { completed_lessons: pendingCompletedLessons },
            token,
            MODULE_OPTIONS
          );
          setMe(synced);
          writePendingCompletedLessons(lessonDraftUserKey, []);
        } catch (syncErr) {
          // Keep pending data for next successful sync.
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load SalonSpeak data.");
    } finally {
      setLoading(false);
    }
  }, [lessonDraftUserKey, token]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (location.pathname === basePath || location.pathname === `${basePath}/`) {
      navigate(`${basePath}/home`, { replace: true });
    }
  }, [location.pathname, navigate, basePath]);

  const openTab = useCallback(
    (tabId) => {
      navigate(`${basePath}/${tabId}`);
    },
    [navigate, basePath]
  );

  const handleToggleFavorite = useCallback(
    async (type, id) => {
      const key = favoriteKey(type, id);
      const already = favorites.has(key);
      const next = new Set(favorites);
      if (already) {
        await removeSalonSpeakFavorite(type, id, token, MODULE_OPTIONS);
        next.delete(key);
      } else {
        await addSalonSpeakFavorite(
          {
            favorite_type: type,
            content_id: id,
          },
          token,
          MODULE_OPTIONS
        );
        next.add(key);
      }
      setFavorites(next);
    },
    [favorites, token]
  );

  const handleSaveAttempt = useCallback(
    async (payload) => {
      const lessonId =
        payload?.attempt_type === "lesson" && payload?.content_id
          ? String(payload.content_id)
          : "";

      if (lessonId) {
        addPendingCompletedLesson(lessonDraftUserKey, lessonId);
        setMe((prev) => mergeMeWithLessons(prev, [lessonId]));
      }

      try {
        await createSalonSpeakAttempt(payload, token, MODULE_OPTIONS);
        await fetchMe();
      } catch (err) {
        if (lessonId) {
          try {
            const synced = await updateSalonSpeakMe(
              { completed_lessons: [lessonId] },
              token,
              MODULE_OPTIONS
            );
            setMe(synced);
            removePendingCompletedLesson(lessonDraftUserKey, lessonId);
            return;
          } catch (syncErr) {
            // Keep queued completion locally; caller should show a visible error state.
            throw syncErr;
          }
        }
        throw err;
      }
    },
    [fetchMe, lessonDraftUserKey, token]
  );

  const handleSaveProgress = useCallback(async () => {
    const queuedLessons = readPendingCompletedLessons(lessonDraftUserKey);
    const completedLessons = normalizeLessonIds(me?.lesson_progress_json?.completed_lessons || []);
    const mergedLessons = normalizeLessonIds([...completedLessons, ...queuedLessons]);
    try {
      const payload = await updateSalonSpeakMe(
        {
          completed_lessons: mergedLessons,
        },
        token,
        MODULE_OPTIONS
      );
      setMe(payload);
      writePendingCompletedLessons(lessonDraftUserKey, []);
      setError("");
      return payload;
    } catch (err) {
      setError("Unable to save lesson progress right now.");
      throw err;
    }
  }, [lessonDraftUserKey, me?.lesson_progress_json?.completed_lessons, token]);

  const handleResetLevelProgress = useCallback(
    async (level) => {
      const targetLevel = Number(level);
      if (!Number.isFinite(targetLevel)) return null;

      const levelLessonIds = normalizeLessonIds(
        (content.lessons || [])
          .filter((lesson) => Number(lesson?.level) === targetLevel)
          .map((lesson) => lesson.id)
      );
      const levelLessonSet = new Set(levelLessonIds);

      const currentCompleted = normalizeLessonIds(me?.lesson_progress_json?.completed_lessons || []);
      const nextCompleted = currentCompleted.filter((lessonId) => !levelLessonSet.has(lessonId));

      try {
        const payload = await updateSalonSpeakMe(
          {
            completed_lessons: nextCompleted,
            replace_completed_lessons: true,
          },
          token,
          MODULE_OPTIONS
        );
        setMe(payload);

        const pending = readPendingCompletedLessons(lessonDraftUserKey);
        writePendingCompletedLessons(
          lessonDraftUserKey,
          pending.filter((lessonId) => !levelLessonSet.has(lessonId))
        );
        setError("");
        return payload;
      } catch (err) {
        setError("Unable to reset this level right now.");
        throw err;
      }
    },
    [lessonDraftUserKey, me?.lesson_progress_json?.completed_lessons, token]
  );

  const handleResetAllProgress = useCallback(async () => {
    try {
      const payload = await updateSalonSpeakMe(
        {
          completed_lessons: [],
          replace_completed_lessons: true,
        },
        token,
        MODULE_OPTIONS
      );
      setMe(payload);
      writePendingCompletedLessons(lessonDraftUserKey, []);
      setError("");
      return payload;
    } catch (err) {
      setError("Unable to reset all levels right now.");
      throw err;
    }
  }, [lessonDraftUserKey, token]);

  const handleDailyGoalChange = useCallback(
    async (dailyGoal) => {
      const payload = await updateSalonSpeakMe(
        { daily_goal: dailyGoal },
        token,
        MODULE_OPTIONS
      );
      setMe(payload);
    },
    [token]
  );

  const handleSalonModeToggle = useCallback(
    async (enabled) => {
      setSalonMode(Boolean(enabled));
      try {
        const payload = await updateSalonSpeakMe(
          { settings: { salon_mode: Boolean(enabled) } },
          token,
          MODULE_OPTIONS
        );
        setMe(payload);
      } catch (err) {
        setError("Unable to save salon mode setting.");
      }
    },
    [token]
  );

  const handleVoiceSelectionChange = useCallback(
    (voiceUri) => {
      const normalizedVoiceUri = String(voiceUri || "").trim().toLowerCase();
      setSelectedVoiceUri(normalizedVoiceUri);
      setSalonSpeakVoicePreference(normalizedVoiceUri);
      setVoiceNotice("");
    },
    []
  );

  const handleSaveVoiceSelection = useCallback(async () => {
    const normalizedVoiceUri = String(selectedVoiceUri || "").trim().toLowerCase();
    if (normalizedVoiceUri === String(savedVoiceUri || "").trim()) return;

    setVoiceSaving(true);
    setVoiceNotice("");
    try {
      const payload = await updateSalonSpeakMe(
        { settings: { speech_voice_uri: normalizedVoiceUri } },
        token,
        MODULE_OPTIONS
      );
      setMe(payload);
      setSavedVoiceUri(normalizedVoiceUri);
      setVoiceNotice("Voice selection saved.");
      setError("");
    } catch (err) {
      setError("Unable to save voice setting.");
    } finally {
      setVoiceSaving(false);
    }
  }, [savedVoiceUri, selectedVoiceUri, token]);

  const nextLesson = useMemo(() => {
    const completed = new Set(me?.lesson_progress_json?.completed_lessons || []);
    return (content.lessons || []).find((lesson) => !completed.has(lesson.id)) || null;
  }, [me?.lesson_progress_json?.completed_lessons]);

  const commonProps = {
    salonMode,
    salonTimezone: setupTimezone || me?.salon_timezone || user?.salon_timezone || "",
    onToggleSalonMode: handleSalonModeToggle,
    onSaveAttempt: handleSaveAttempt,
    onSaveProgress: handleSaveProgress,
    onResetLevelProgress: handleResetLevelProgress,
    onResetAllProgress: handleResetAllProgress,
  };

  const renderTabContent = () => {
    if (activeTab === "home") {
      return (
        <SalonSpeakHome
          firstName={firstName || "there"}
          streakCount={me?.streak_count || 0}
          dailyGoal={me?.daily_goal || 3}
          completedLessonsToday={me?.completed_lessons_today || 0}
          nextLesson={nextLesson}
          onOpenTab={openTab}
          onDailyGoalChange={handleDailyGoalChange}
          selectedVoiceUri={selectedVoiceUri}
          onVoiceSelectionChange={handleVoiceSelectionChange}
          onSaveVoiceSelection={handleSaveVoiceSelection}
          isVoiceSelectionDirty={
            String(selectedVoiceUri || "").trim() !== String(savedVoiceUri || "").trim()
          }
          voiceSaving={voiceSaving}
          voiceNotice={voiceNotice}
        />
      );
    }
    if (activeTab === "lessons") {
      return (
        <SalonSpeakLessons
          lessons={content.lessons || []}
          lessonProgress={me?.lesson_progress_json || {}}
          {...commonProps}
        />
      );
    }
    if (activeTab === "roleplay") {
      return (
        <SalonSpeakRoleplay
          scenarios={content.roleplayScenarios || []}
          {...commonProps}
        />
      );
    }
    if (activeTab === "vocab") {
      return (
        <SalonSpeakVocabulary
          vocabulary={content.vocabulary || []}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
          {...commonProps}
        />
      );
    }
    if (activeTab === "phrases") {
      return (
        <SalonSpeakQuickPhrases
          phrases={content.quickPhrases || []}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
        />
      );
    }
    return null;
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="hrSectionShell">
      <div className="hrSectionWide hr-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">SalonSpeak Reception</h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6 pb-24 md:pb-6">
            <ReportHelpCard
              title="Help Guide: SalonSpeak Reception"
              subtitle="Use this guide to run daily speaking practice with clear scoring and roleplay feedback."
              sections={SALON_SPEAK_HELP_SECTIONS}
              troubleshooting={SALON_SPEAK_TROUBLESHOOTING}
            />

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              {SALON_SPEAK_TOOLTIPS.tile}
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
              <aside className="hidden md:block rounded-2xl border border-slate-200 bg-white p-3 shadow-sm h-fit">
                <div className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => openTab(tab.id)}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold ${
                        activeTab === tab.id
                          ? "bg-slate-900 text-white"
                          : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </aside>

              <section>
                {loading ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
                    Loading SalonSpeak Reception...
                  </div>
                ) : (
                  renderTabContent()
                )}
              </section>
            </div>
          </div>
        </div>
      </div>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white shadow-[0_-4px_12px_rgba(15,23,42,0.08)]">
        <div className="mx-auto max-w-[560px] grid grid-cols-5 gap-1 px-2 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => openTab(tab.id)}
              className={`rounded-lg px-1 py-2 text-[11px] font-semibold ${
                activeTab === tab.id
                  ? "bg-slate-900 text-white"
                  : "text-slate-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
